import React, { useMemo, useState } from 'react';
import { App, Button, Card, Col, Descriptions, Row, Space, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, PrinterOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { PrintableHrmsTransaction } from '@/components/hr/PrintableHrmsTransaction';
import { ENTITY_TYPE_HR_OT_REQUEST } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';
import { openHrmsPrintOverlay } from './shared/openHrmsPrintOverlay';

const { Text } = Typography;

const OvertimeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions();
  const [loading, setLoading] = useState<string | null>(null);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.otRequest(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getOtRequest(id!),
        () => hrmsService.listOtRequests(),
        id!
      ),
    enabled: Boolean(id),
  });

  const workflow = useMemo(
    () => buildLinearWorkflow(['Draft', 'Pending', 'Approved'], row?.status || 'Draft'),
    [row?.status]
  );

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="OT request" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="OT request" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setLoading(key);
    try {
      await fn();
      message.success(ok);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.otRequest(id!) });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.otRequests() });
    } catch {
      message.error(`${ok} failed`);
    } finally {
      setLoading(null);
    }
  };

  const printDoc = () => {
    openHrmsPrintOverlay((onClose) => (
      <PrintableHrmsTransaction
        onClose={onClose}
        data={{
          docType: 'HR_OT_REQUEST',
          title: 'Overtime Request',
          documentNumber: row.ot_number,
          employee_name: empLabel(row.employee_id),
          status: row.status,
          meta: [
            {
              label: 'OT date',
              value: row.ot_date ? dayjs(row.ot_date).format('DD-MMM-YYYY') : '—',
            },
            { label: 'OT kind', value: String(row.ot_kind || 'Normal') },
          ],
          lines: [
            { description: 'Requested hours', value: String(row.requested_hours ?? '—') },
            { description: 'Approved hours', value: String(row.approved_hours ?? '—') },
            { description: 'Excess hours', value: String(row.excess_hours ?? '—') },
            { description: 'Disposition', value: String(row.excess_disposition || '—') },
            { description: 'Reason', value: String(row.reason || '—') },
          ],
          notes: row.reason,
        }}
      />
    ));
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="OT request"
          documentNumber={row.ot_number  || '—'}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          subtitle={<Text type="secondary">{empLabel(row.employee_id)}</Text>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/overtime')}>
                Back
              </Button>
              <Button icon={<PrinterOutlined />} onClick={printDoc}>
                Print
              </Button>
              {(row.status === 'Draft') && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={loading === 'submit'}
                  onClick={() => run('submit', () => hrmsService.submitOt(id!), 'Submitted')}
                >
                  Submit
                </Button>
              )}
              {(row.status === 'Draft' || row.status === 'Pending') && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={loading === 'approve'}
                  onClick={() => run('approve', () => hrmsService.approveOtRequest(id!), 'Approved')}
                >
                  Approve
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Details" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">{empLabel(row.employee_id)}</Descriptions.Item>
              <Descriptions.Item label="Date">
                {row.ot_date ? dayjs(row.ot_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Requested hours">{row.requested_hours}</Descriptions.Item>
              <Descriptions.Item label="Approved hours">{row.approved_hours ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="OT kind">{row.ot_kind || 'Normal'}</Descriptions.Item>
              <Descriptions.Item label="Excess">{row.excess_hours ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Disposition">{row.excess_disposition || '—'}</Descriptions.Item>
              <Descriptions.Item label="Reason" span={2}>
                {row.reason || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <EntityAttachmentsCard entityType="HR_OT_REQUEST" entityId={row.id} />
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_OT_REQUEST}
              entityId={row.id}
              extraInvalidateQueryKeys={[hrmsKeys.otRequest(id!), hrmsKeys.otRequests()]}
            />
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default OvertimeDetailPage;
